import type { GenericFieldConfig } from '@neo/blocks/forms/types'
import { Separator } from '@neo/primitives/separator'
import type { UserInputNodeData, SidebarComponentProps } from '../types'
import { v2validations } from '../config/validation.config'
import { GenericSidebarComponent } from './sidebar.base'
import { NodevarList } from '../components/nodevar-list'

type FormValues = UserInputNodeData['config']

const fields = (nodeVars: string[]): GenericFieldConfig<FormValues> => {
  const validations = v2validations({ nodeVars }).user_input
  return [
    {
      name: 'name',
      label: 'Name',
      zodValidation: validations.shape.name,
      componentType: 'input',
    },
    {
      name: 'prompt',
      label: 'Prompt',
      zodValidation: validations.shape.prompt,
      componentType: 'textarea',
      inputProps: {
        endIcon: (
          <NodevarList nodevars={nodeVars} className="pr-2" inputElementSelector="[name=prompt]" />
        ),
      },
    },
    {
      name: 'optional',
      label: 'Mark this input as optional',
      componentType: 'checkbox',
      zodValidation: validations.shape.optional,
    },
    {
      name: 'collect_inputs',
      type: 'slot',
      component: <div className="text-p-500 mt-4 text-secondary">Collect inputs</div>,
    },
    {
      name: 'input_validation',
      label: 'Input Validation',
      zodValidation: validations.shape.input_validation,
      componentType: 'select',
      inputProps: {
        options: [
          { label: 'Any', value: 'any' },
          { label: 'Alphanumeric', value: 'alphanumeric' },
          { label: 'Email', value: 'email' },
          { label: 'Number', value: 'integer' },
          { label: 'Phone number', value: 'phone_number' },
          { label: 'URL', value: 'url' },
          { label: 'Date', value: 'date' },
        ],
      },
    },
    {
      name: 'placeholder_text',
      label: 'Placeholder Text',
      zodValidation: validations.shape.placeholder_text,
      componentType: 'input',
      inputProps: {
        placeholder: 'Enter placeholder text',
      },
    },
    {
      name: 'contact_identifier',
      label: 'Use this data as customer identifier',
      componentType: 'checkbox',
      zodValidation: validations.shape.contact_identifier,
      onChangeListenTo: ['input_validation'],
      visible: (values) =>
        values.input_validation === 'phone_number' || values.input_validation === 'email',
    },
    {
      name: 'separator',
      type: 'slot',
      element: () => (
        <Separator orientation="horizontal" className="h-px w-full bg-bg1 col-span-2" />
      ),
    },
    {
      name: 'reply_timeout',
      label: 'Reply Timeout',
      zodValidation: validations.shape.reply_timeout,
      componentType: 'inputNumber',
      inputProps: {
        type: 'number',
        min: 60,
        max: 86400,
        defaultValue: 3600,
        endIcon: <span className="text-tertiary text-p-400 mr-7">Seconds</span>,
      },
    },
  ]
}

// NODE_GEN: Update this code, do not commit directly
// Remove this comment after updating the code

export const UserInputSidebarComponent = (props: SidebarComponentProps<UserInputNodeData>) => {
  const { nodeVars } = props
  const formFields = fields(nodeVars)

  return <GenericSidebarComponent fields={formFields} {...props} />
}
